import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage, getApiErrorStatus } from '@/shared/api/axios';
import { formatINR } from '@/shared/utils/currency';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { useChartOfAccountsList } from '@/features/accounts/hooks/useChartOfAccounts';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { PlusCircle, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

function useContraEntries() {
  return useQuery({ queryKey: ['accounts', 'contra'], queryFn: async () => { const { data } = await api.get('/api/accounts/contra'); return data as any[]; } });
}

function useCreateContra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => {
      const { data } = await api.post('/api/accounts/contra', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts', 'contra'] });
      toast.success('Contra transfer posted successfully!');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || 'Failed to post transfer';
      toast.error(msg);
    },
  });
}

// The backend accepts exactly these legacy codes (src/api-accounts/contra.js:21).
// They were hardcoded here too, so the picker offered accounts that may not exist
// in THIS company's chart of accounts and the submit failed after the amount was
// typed. The options are now the intersection of the accepted codes and the
// tenant's real chart, so an unconfigured chart says so up front.
const CONTRA_CODES = ['1010', '1020', '1030'];
const CODE_LABELS: Record<string, string> = { '1010': 'Cash', '1020': 'Bank', '1030': 'Petty Cash' };

export function ContraEntries() {
  const { data: entries, isLoading, error } = useContraEntries();
  const createMutation = useCreateContra();
  const coa = useChartOfAccountsList({ active: 'true' });
  const options = ((coa.data ?? []) as any[])
    .filter((a) => CONTRA_CODES.includes(String(a.code)))
    .map((a) => ({ value: String(a.code), label: `${CODE_LABELS[String(a.code)] || a.name} (${a.code})` }));
  const [isOpen, setIsOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  // Seeded from the real chart rather than trusting that codes 1010/1020 exist
  // here: a company that named its cash account differently used to post against
  // an account that is not in its own books.
  const fromValue = options.some((o) => o.value === from) ? from : (options[0]?.value ?? '');
  const toValue = options.some((o) => o.value === to) ? to : (options[1]?.value ?? fromValue);
  const canTransfer = options.length >= 2 && !coa.isLoading;
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [narration, setNarration] = useState('');

  const handleCreate = async () => {
    if (!canTransfer) { toast.error('This chart of accounts has fewer than two of the cash/bank accounts a contra entry needs.'); return; }
    if (!amount || parseFloat(amount) <= 0) { toast.error('Amount must be greater than 0'); return; }
    if (fromValue === toValue) { toast.error('Source and destination accounts must be different'); return; }
    try {
      await createMutation.mutateAsync({ fromAccountCode: fromValue, toAccountCode: toValue, amount: parseFloat(amount), date, narration });
      setIsOpen(false);
      setAmount('');
      setNarration('');
    } catch (_) {
      // error handled by mutation onError
    }
  };

  if (isLoading) return <div aria-busy="true" className="space-y-2"><Skeleton className="h-12 w-full rounded-card" /><Skeleton className="h-[300px] w-full rounded-card" /></div>;

  // 403 and a failed read are not "no transfers yet" — the reader would take
  // either one as a confirmed nil contra balance.
  if (error) {
    const denied = getApiErrorStatus(error) === 403;
    return (
      <div role="alert" className={`rounded-card border p-4 text-sm ${denied ? 'border-warning/25 bg-warning/10 text-warning-text' : 'border-danger/20 bg-danger/10 text-danger-text'}`}>
        <p className="font-semibold">{denied ? 'Permission denied' : 'Could not load contra entries'}</p>
        <p className="mt-1 text-body-sm">{denied
          ? 'Your role cannot read the cash and bank ledger. Ask an Accounts administrator for read access.'
          : getApiErrorMessage(error, 'The contra register did not respond. Retry from the Accounts page.')}</p>
      </div>
    );
  }

  return (
    <Card className="shadow-xs">
      <CardHeader title="Contra Entries (Cash ↔ Bank Transfers)" subtitle="Every transfer posted between cash, bank and petty cash." action={<Button size="sm" disabled={!canTransfer} title={canTransfer ? undefined : "This chart of accounts does not expose at least two of Cash (1010), Bank (1020) or Petty Cash (1030)."} onClick={() => setIsOpen(true)}><PlusCircle className="h-4 w-4 mr-1" /> New Transfer</Button>} />
      <CardBody className="p-0">
        <div className="overflow-auto max-h-[400px]">
          <table className="w-full text-body-sm text-left">
            <thead className="text-label-caps uppercase text-on-surface-variant bg-muted sticky top-0">
              <tr><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">From → To</th><th className="px-4 py-2.5 text-right">Amount</th><th className="px-4 py-2.5">Narration</th><th className="px-4 py-2.5">By</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(!entries || entries.length === 0) && <tr><td colSpan={5}><EmptyState title="No contra transfers" description="Move funds between cash, bank or petty cash and the transfer posts here." action={<Button size="sm" variant="outline" disabled={!canTransfer} onClick={() => setIsOpen(true)}>New Transfer</Button>} /></td></tr>}
              {entries?.map((e: any, i: number) => (
                <tr key={e.transactionId || i} className="hover:bg-muted/50">
                  <td className="px-4 py-2.5 font-mono tabular-nums text-muted-foreground">{new Date(e.date).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-2.5 flex items-center gap-1">{e.fromAccount} <ArrowRight className="h-3 w-3 text-muted-foreground" /> {e.toAccount}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums font-medium">{e.amount == null ? '—' : formatINR(e.amount)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground truncate max-w-[200px]">{e.narration || '—'}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{e.createdBy || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
      <Modal open={isOpen} onClose={() => setIsOpen(false)} title="New Contra Entry (Transfer)" size="sm" footer={
        <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button><Button onClick={handleCreate} disabled={createMutation.isPending}>{createMutation.isPending ? 'Posting...' : 'Transfer'}</Button></div>
      }>
        <div className="space-y-4 py-4">
          <div><label htmlFor="contra-from" className="text-sm font-medium">From Account</label><Select id="contra-from" name="from" value={fromValue} onChange={e => setFrom(e.target.value)} options={options} /></div>
          <div><label htmlFor="contra-to" className="text-sm font-medium">To Account</label><Select id="contra-to" name="to" value={toValue} onChange={e => setTo(e.target.value)} options={options} /></div>
          <div><label htmlFor="contra-amount" className="text-sm font-medium">Amount (₹)</label><Input id="contra-amount" name="amount" type="number" value={amount} onChange={e => setAmount(e.target.value)} className="text-right font-mono tabular-nums" /></div>
          <div><label htmlFor="contra-date" className="text-sm font-medium">Date</label><Input id="contra-date" name="date" type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
          <div><label htmlFor="contra-narration" className="text-sm font-medium">Narration</label><Input id="contra-narration" name="narration" value={narration} onChange={e => setNarration(e.target.value)} placeholder="Optional" /></div>
        </div>
      </Modal>
    </Card>
  );
}
